/**
 * Traductores de result/error de jobs MikroTik a bullets legibles.
 *
 * Cada job devuelve un `result` con una forma particular (ver
 * `api/app/services/mikrotik_reconcile.py`). Acá traducimos esa estructura a
 * bullets humanas ("Secret PPPoE: ya existía, sin cambios"), para que el
 * operador no tenga que leer JSON crudo en el drawer.
 *
 * Si la forma del result cambia en el backend, actualizar acá también.
 */
import type { JobAction, MikrotikJob } from "./schemas";

export type BulletTone = "ok" | "warn" | "info" | "error";

export type Bullet = {
  tone: BulletTone;
  text: string;
  detail?: string;
};

type ResultDict = Record<string, unknown>;

// ── Helpers tipados para leer ruta con defaults ────────────────────────────
function asDict(v: unknown): ResultDict | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as ResultDict) : null;
}

function asNum(v: unknown, fallback = 0): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function asBool(v: unknown): boolean {
  return v === true;
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

// ── reconcile_contract ─────────────────────────────────────────────────────
function translateReconcileContract(result: ResultDict): Bullet[] {
  const out: Bullet[] = [];
  const success = asBool(result.success);

  const secret = asDict(result.secret);
  const profile = asDict(result.profile);
  const addrList = asDict(result.address_list);
  const anomalies = asArray(result.anomalies).filter((a) => typeof a === "string") as string[];

  // Secret
  if (secret) {
    const found = asNum(secret.matches_found);
    const deleted = asNum(secret.deleted);
    const created = asNum(secret.created);
    const activeKilled = asBool(secret.active_killed);
    const parts: string[] = [];
    if (found === 0 && created === 1) parts.push("no existía, se creó");
    else if (found === 1 && deleted === 1 && created === 1) parts.push("recreado (limpieza idempotente)");
    else if (found > 1 && deleted > 0 && created === 1) parts.push(`${found} matches, ${deleted} limpiados, 1 recreado`);
    else if (found === 1 && created === 0 && deleted === 0) parts.push("ya existía en estado correcto");
    if (activeKilled) parts.push("sesión PPPoE activa tumbada");
    out.push({
      tone: "ok",
      text: `Secret PPPoE: ${parts.join(" · ") || "sin cambios"}`,
    });
  }

  // Profile
  if (profile) {
    const found = asNum(profile.matches_found);
    const deleted = asNum(profile.deleted);
    const created = asNum(profile.created);
    let text: string;
    if (found === 0 && created === 1) text = "no existía en el router, se creó";
    else if (found >= 1 && created === 1) text = `${deleted} anterior${deleted === 1 ? "" : "es"} borrado${deleted === 1 ? "" : "s"} y recreado`;
    else if (found === 1 && created === 0) text = "ya existía con rate-limit correcto, sin cambios";
    else text = "sin cambios";
    out.push({ tone: "ok", text: `Perfil de velocidad: ${text}` });
  }

  // Address list (suspendidos)
  if (addrList) {
    const found = asNum(addrList.matches_found);
    const deleted = asNum(addrList.deleted);
    const created = asNum(addrList.created);
    let text: string | null = null;
    if (created === 1 && deleted === 0) text = "entrada agregada (contrato suspendido)";
    else if (deleted >= 1 && created === 0) text = `${deleted} entrada${deleted === 1 ? "" : "s"} removida${deleted === 1 ? "" : "s"} (contrato activo)`;
    else if (found === 0 && created === 0 && deleted === 0) text = "sin entradas (contrato activo)";
    else if (found === 1 && created === 0 && deleted === 0) text = "ya estaba correcta";
    if (text) out.push({ tone: "ok", text: `Address list 'suspendidos': ${text}` });
  }

  // Anomalías
  if (anomalies.length > 0) {
    for (const a of anomalies) out.push({ tone: "warn", text: `Anomalía: ${a}` });
  } else {
    out.push({ tone: "info", text: "Sin anomalías detectadas" });
  }

  // Success final
  if (!success) {
    const err = asStr(result.error);
    out.unshift({
      tone: "error",
      text: "Reconciliación falló",
      detail: err ?? "Ver JSON crudo para más contexto",
    });
  }

  return out;
}

// ── delete_contract ────────────────────────────────────────────────────────
function translateDeleteContract(result: ResultDict): Bullet[] {
  const out: Bullet[] = [];
  const success = asBool(result.success);
  const secret = asDict(result.secret);
  const addrList = asDict(result.address_list);
  const anomalies = asArray(result.anomalies).filter((a) => typeof a === "string") as string[];

  if (secret) {
    const found = asNum(secret.matches_found);
    const deleted = asNum(secret.deleted);
    const activeKilled = asBool(secret.active_killed);
    const parts: string[] = [];
    if (found === 0) parts.push("no había secret en el router (nada que borrar)");
    else parts.push(`${deleted}/${found} secret${found === 1 ? "" : "s"} borrado${deleted === 1 ? "" : "s"}`);
    if (activeKilled) parts.push("sesión activa tumbada");
    out.push({ tone: "ok", text: `Secret PPPoE: ${parts.join(" · ")}` });
  }

  if (addrList) {
    const found = asNum(addrList.matches_found);
    const deleted = asNum(addrList.deleted);
    let text: string;
    if (found === 0) text = "sin entradas que limpiar";
    else text = `${deleted}/${found} entrada${found === 1 ? "" : "s"} removida${deleted === 1 ? "" : "s"}`;
    out.push({ tone: "ok", text: `Address list: ${text}` });
  }

  if (anomalies.length > 0) {
    for (const a of anomalies) out.push({ tone: "warn", text: `Anomalía: ${a}` });
  } else {
    out.push({ tone: "info", text: "Limpieza completa sin residuos" });
  }

  if (!success) {
    out.unshift({
      tone: "error",
      text: "Borrado falló",
      detail: asStr(result.error) ?? undefined,
    });
  }

  return out;
}

// ── reconcile_router_firewall ──────────────────────────────────────────────
// Set de suspensión: 3 reglas en orden top-down
//   1) suspend-allow-icmp  — accept ICMP del CPE (responses para ping NOC→CPE)
//   2) suspend-allow-snmp  — accept UDP src-port=161 del CPE (responses SNMP)
//   3) suspend-drop        — drop catch-all del CPE suspendido
// Cliente suspendido sigue siendo monitoreable desde el NOC pero sin servicio.
function translateReconcileFirewall(result: ResultDict): Bullet[] {
  const out: Bullet[] = [];
  const success = asBool(result.success);
  const rule = asDict(result.filter_rule);
  const anomalies = asArray(result.anomalies).filter((a) => typeof a === "string") as string[];

  if (rule) {
    const found = asNum(rule.matches_found);
    const deleted = asNum(rule.deleted);
    const created = asNum(rule.created);
    const expected = asNum(rule.expected_count) || 3;
    let text: string;
    if (found === 0 && created === expected) {
      text = `no existían, se crearon ${created} reglas del set de suspensión (allow ICMP + allow SNMP + drop)`;
    } else if (found === expected && created === 0 && deleted === 0) {
      text = `set completo y en orden correcto (${expected} reglas)`;
    } else if (created === expected && deleted >= 1) {
      text = `${deleted} regla(s) anterior(es) limpiadas y se recrearon ${created} en orden correcto`;
    } else if (created > 0 || deleted > 0) {
      text = `${created} regla(s) creada(s), ${deleted} regla(s) borrada(s) — set ahora con ${expected} reglas en orden`;
    } else {
      text = "sin cambios";
    }
    out.push({ tone: "ok", text: `Firewall suspensión: ${text}` });
  }

  if (anomalies.length > 0) {
    for (const a of anomalies) out.push({ tone: "warn", text: `Anomalía: ${a}` });
  } else {
    out.push({ tone: "info", text: "Firewall en estado esperado" });
  }

  if (!success) {
    out.unshift({
      tone: "error",
      text: "Reconciliación de firewall falló",
      detail: asStr(result.error) ?? undefined,
    });
  }

  return out;
}

// ── reconcile_full_router ──────────────────────────────────────────────────
function translateReconcileFullRouter(result: ResultDict): Bullet[] {
  const out: Bullet[] = [];
  const success = asBool(result.success);
  const processed = asNum(result.contracts_processed);
  const ok = asNum(result.contracts_ok);
  const err = asNum(result.contracts_error);
  const filter = asDict(result.filter_rule);
  const perContract = asArray(result.per_contract);
  const anomalies = asArray(result.anomalies).filter((a) => typeof a === "string") as string[];

  // Firewall (set de suspensión: 3 reglas ICMP-allow + SNMP-allow + drop)
  if (filter) {
    const frule = asNum(filter.matches_found);
    const fcreate = asNum(filter.created);
    const fdel = asNum(filter.deleted);
    const fexp = asNum(filter.expected_count) || 3;
    let text: string;
    if (fcreate === fexp && frule === 0) text = `creadas las ${fexp} reglas (faltaban)`;
    else if (fcreate === fexp && fdel >= 1) text = `${fdel} regla(s) anterior(es) limpiadas y recreadas ${fcreate} en orden`;
    else if (fcreate > 0 || fdel > 0) text = `${fcreate} creada(s), ${fdel} borrada(s)`;
    else text = `set completo y en orden (${fexp} reglas), sin cambios`;
    out.push({ tone: "ok", text: `Firewall suspensión: ${text}` });
  }

  // Contratos
  out.push({
    tone: err === 0 ? "ok" : "warn",
    text: `Contratos procesados: ${processed} · OK: ${ok} · con error: ${err}`,
  });

  // Listado resumido de contratos con problema
  const failed = perContract
    .map((pc) => asDict(pc))
    .filter((pc): pc is ResultDict => !!pc && !asBool(pc.success));
  if (failed.length > 0 && failed.length <= 5) {
    for (const f of failed) {
      const codigo = asStr(f.codigo) ?? asStr(f.contrato_id)?.slice(0, 8) ?? "?";
      const err = asStr(f.error) ?? "sin detalle";
      out.push({ tone: "error", text: `Falló contrato ${codigo}`, detail: err });
    }
  } else if (failed.length > 5) {
    out.push({ tone: "error", text: `${failed.length} contratos fallaron (ver JSON crudo abajo)` });
  }

  // Anomalías (agregadas del pass completo)
  if (anomalies.length > 0 && anomalies.length <= 5) {
    for (const a of anomalies) out.push({ tone: "warn", text: `Anomalía: ${a}` });
  } else if (anomalies.length > 5) {
    out.push({ tone: "warn", text: `${anomalies.length} anomalías detectadas (ver JSON crudo)` });
  } else if (err === 0) {
    out.push({ tone: "info", text: "Sin anomalías detectadas" });
  }

  if (!success && processed === 0) {
    out.unshift({
      tone: "error",
      text: "Reconciliación global falló",
      detail: asStr(result.error) ?? "No se pudo conectar al router o error general",
    });
  }

  return out;
}

// ── Traductor de errores técnicos a prosa ──────────────────────────────────
export function translateJobError(error: string, errorCode: string | null): Bullet {
  const lower = error.toLowerCase();
  if (errorCode === "connection_timeout" || lower.includes("timeout")) {
    return {
      tone: "error",
      text: "No se pudo conectar al router: timeout",
      detail: "El router no respondió dentro del tiempo límite. Verifica que esté en línea y accesible desde el API.",
    };
  }
  if (errorCode === "connection_refused" || lower.includes("refused")) {
    return {
      tone: "error",
      text: "Conexión rechazada por el router",
      detail: "El router está en línea pero rechaza la conexión al puerto API. Verifica el puerto y que la API esté habilitada.",
    };
  }
  if (lower.includes("authentication") || lower.includes("invalid user") || lower.includes("login")) {
    return {
      tone: "error",
      text: "Autenticación falló contra el router",
      detail: "Usuario o contraseña del router incorrectos, o el usuario no tiene permisos de escritura.",
    };
  }
  if (lower.includes("dns") || lower.includes("resolve")) {
    return {
      tone: "error",
      text: "No se pudo resolver el host del router",
      detail: "La dirección configurada del router no resuelve a una IP. Revisa los ajustes del router.",
    };
  }
  // Fallback: devolver el mensaje tal cual
  return { tone: "error", text: error, detail: errorCode ? `code: ${errorCode}` : undefined };
}

// ── Punto de entrada ──────────────────────────────────────────────────────
export function translateJobResult(job: MikrotikJob): Bullet[] {
  // Si el job falló con error HTTP-level (no alcanzó a producir result
  // estructurado), priorizamos traducir el error.
  if (job.status === "error" && job.error && !job.result) {
    return [translateJobError(job.error, job.error_code)];
  }
  if (!job.result) {
    if (job.status === "queued") {
      return [{ tone: "info", text: "Encolado, aún no ha corrido" }];
    }
    if (job.status === "running") {
      return [{ tone: "info", text: "Ejecutándose…" }];
    }
    return [{ tone: "info", text: "Sin resultado registrado" }];
  }

  const action = job.action as JobAction;
  switch (action) {
    case "reconcile_contract":
      return translateReconcileContract(job.result);
    case "delete_contract":
      return translateDeleteContract(job.result);
    case "reconcile_router_firewall":
      return translateReconcileFirewall(job.result);
    case "reconcile_full_router":
      return translateReconcileFullRouter(job.result);
    default:
      return [
        {
          tone: "info",
          text: `Acción desconocida: ${action}`,
          detail: "Ver JSON crudo abajo",
        },
      ];
  }
}
